import React, { useEffect, useState, useCallback } from "react";
import { AlertTriangle, ShieldCheck, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

/**
 * SystemHealthBanner — replaces the always-on red FeatureFlagBanner.
 *
 * Fetches the consolidated `/admin/system-health` on mount and renders one of
 * three UIs:
 *
 *   • RED banner — Stripe unreachable, key mode mismatches env, charges
 *                  disabled, or PAYMENTS_LIVE=true with no key.
 *                  Lists each issue and stays visible with an X to dismiss.
 *   • YELLOW banner — non-fatal issues (e.g. webhook secret missing).
 *   • GREEN pill — payments live AND Stripe verified healthy. Small, subtle
 *                  indicator, not a full banner.
 *   • Nothing — payments off, no admin, or env is preview with expected state.
 *
 * Dismissals are stored in localStorage keyed by a signature of the current
 * issue set, so genuinely NEW problems always resurface even after the user
 * has closed a previous warning.
 */
const DISMISS_KEY = "talentsio.system_health.dismissed_signature";

const SystemHealthBanner = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [dismissedSig, setDismissedSig] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) || ""; } catch { return ""; }
  });

  useEffect(() => {
    if (!user || user.role !== "admin") return undefined;
    let alive = true;
    api.get("/admin/system-health")
      .then(({ data: d }) => { if (alive) setData(d); })
      .catch(() => { /* fail-quiet — never crash the shell on a health probe */ });
    return () => { alive = false; };
  }, [user]);

  const signature = data?.issues?.map((i) => i.code).sort().join("|") || "";

  const dismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, signature); } catch { /* ignore */ }
    setDismissedSig(signature);
  }, [signature]);

  if (!user || user.role !== "admin" || !data) return null;

  const isDismissed = signature && signature === dismissedSig;

  // Green pill — payments healthy in production. Non-intrusive.
  if (data.status === "green" && data.payments_live && data.environment === "production") {
    return (
      <div
        className="px-4 lg:px-6 pt-3"
        data-testid="system-health-green-pill"
      >
        <div className="inline-flex items-center gap-2 text-[12px] text-[#2fbe6e] bg-[#0f2418] border border-[#1a4a2f] rounded-full px-3 py-1">
          <ShieldCheck size={13} />
          <span className="font-medium">Payments healthy</span>
          <span className="text-neutral-500">·</span>
          <span className="text-neutral-400">
            {data.stripe?.mode?.toUpperCase()} mode · score {data.score}/100
          </span>
        </div>
      </div>
    );
  }

  // Nothing to warn about — payments off or preview env behaving as expected.
  if (data.status === "green" || isDismissed) return null;

  const isRed = data.status === "red";
  const tone = isRed
    ? { bg: "bg-[#CC1F1F]", border: "border-b-2 border-[#7a0e0e]", label: "Production safety alert" }
    : { bg: "bg-[#8a5a00]", border: "border-b-2 border-[#4a3200]", label: "Payments configuration warning" };

  return (
    <div
      className={`${tone.bg} text-white px-6 py-3 ${tone.border} flex items-start gap-3`}
      data-testid={isRed ? "system-health-red-banner" : "system-health-yellow-banner"}
    >
      <AlertTriangle size={18} className="shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0 text-[13px] leading-snug">
        <div className="font-semibold uppercase tracking-wider opacity-90 mb-0.5">
          {tone.label}
        </div>
        <ul className="space-y-0.5" data-testid="system-health-issue-list">
          {data.issues?.map((iss) => (
            <li key={iss.code} data-testid={`system-health-issue-${iss.code}`}>
              {iss.message}
            </li>
          ))}
        </ul>
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss warning"
        data-testid="system-health-dismiss-btn"
        className="shrink-0 -mt-0.5 -mr-1 p-1.5 rounded hover:bg-white/10 transition-colors"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default SystemHealthBanner;
